import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import styled from "styled-components";
import AuthShell, { AuthFooter, InlineCode, PasswordBox } from "./AuthShell";
import { FiArrowRight, FiMail, FiEye, FiEyeOff } from "react-icons/fi";
import { FaSteam } from "react-icons/fa";
import { FormField, Divider, Badge } from "./shared";
import toast from "react-hot-toast";
import { useAuthStore } from "../../store/authStore";
import { useLanguage } from "../../contexts/LanguageContext";
import { localizeAuthError } from "../../lib/apiErrors";
import { clearStoredRef, storedRef } from "../../lib/referral";
import {
  Field,
  Action,
  Note,
  request,
  acceptSession,
  steamLogin,
  ApiError,
} from "./shared";
const CODE_RESEND_SECONDS = 60;

const ConsentRow = styled.label`
  display: grid;
  grid-template-columns: 20px 1fr;
  gap: 10px;
  align-items: start;
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;
  input {
    width: 20px;
    height: 20px;
    margin: 0;
    accent-color: ${(p) => p.theme.colors.primary};
  }
  a {
    text-decoration: underline;
  }
`;
const DocLink = ({ to, children }: { to: string; children: React.ReactNode }) => (
  <a href={to} target="_blank" rel="noopener noreferrer">
    {children}
  </a>
);

export function EmailAuth({ mode }: { mode: "register" | "password" }) {
  const nav = useNavigate();
  const { t } = useLanguage();
  const [email, setEmail] = useState(""),
    [username, setUsername] = useState(""),
    [password, setPassword] = useState(""),
    [code, setCode] = useState(""),
    [busy, setBusy] = useState(false),
    [sent, setSent] = useState(false),
    [cooldown, setCooldown] = useState(0),
    [errors, setErrors] = useState<Record<string, string>>({}),
    [formError, setFormError] = useState(""),
    [showPassword, setShowPassword] = useState(false),
    [agreeTerms, setAgreeTerms] = useState(false),
    [agreePd, setAgreePd] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  function consentErrors(next: Record<string, string>) {
    if (mode !== "register") return;
    if (!agreeTerms) next.terms = t("auth.err.consent_terms");
    if (!agreePd) next.pd = t("auth.err.consent_pd");
  }
  async function send() {
    const next: Record<string, string> = {};
    consentErrors(next);
    if (!validEmail) next.email = t("auth.err.email");
    if (mode === "register" && !username.trim()) next.username = t("auth.err.username");
    setErrors(next);
    if (Object.keys(next).length) return;
    try {
      setBusy(true);
      setFormError("");
      await request("/auth/code", { email: email.trim(), purpose: mode });
      setSent(true);
      setCooldown(CODE_RESEND_SECONDS);
    } catch (e) {
      setFormError(localizeAuthError((e as Error).message, t, e instanceof ApiError ? e.status : undefined, e instanceof ApiError ? e.code : undefined));
    } finally {
      setBusy(false);
    }
  }
  function changeEmail() {
    setSent(false);
    setCode("");
    setCooldown(0);
    setErrors({});
    setFormError("");
  }
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!sent) {
      await send();
      return;
    }
    const next: Record<string, string> = {};
    if (!/^\d{6}$/.test(code.trim())) next.code = t("auth.err.code");
    if (!password) next.password = t("auth.err.password");
    else if (password.length > 72) next.password = t("auth.err.password_long");
    consentErrors(next);
    setErrors(next);
    if (Object.keys(next).length) return;
    setBusy(true);
    setFormError("");
    try {
      const data = await request(
        mode === "register" ? "/auth/register" : "/auth/password",
        { email: email.trim(), username: username.trim(), password, code: code.trim(), ...(mode === "register" ? { ref: storedRef(), terms: agreeTerms, personalData: agreePd } : {}) },
      );
      if (mode === "register") {
        clearStoredRef();
        acceptSession(data);
        nav("/");
      } else {
        toast.success(t("auth.password_saved"));
        nav("/login");
      }
    } catch (e) {
      setFormError(localizeAuthError((e as Error).message, t, e instanceof ApiError ? e.status : undefined, e instanceof ApiError ? e.code : undefined));
    } finally {
      setBusy(false);
    }
  }
  const errText = (key: string) =>
    errors[key] ? (
      <small id={`err-${key}`} role="alert" style={{ color: "#ff9191" }}>
        {errors[key]}
      </small>
    ) : null;
  return (
    <AuthShell
      title={mode === "register" ? t("auth.register.title") : t("auth.reset.title")}
      description={mode === "register" ? t("auth.register.lead") : t("auth.reset.lead")}
    >
      <form onSubmit={submit} noValidate>
        <Note aria-live="polite">
          {t("auth.step", { n: sent ? 2 : 1 })} ·{" "}
          {sent ? t("auth.step2") : t("auth.step1")}
        </Note>
        <FormField>
          Email
          <Field
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            value={email}
            readOnly={sent}
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? "err-email" : undefined}
            onChange={(e) => setEmail(e.target.value)}
          />
          {errText("email")}
        </FormField>
        {mode === "register" && (
          <FormField>
            {t("auth.username")}
            <Field
              autoComplete="nickname"
              placeholder={t("auth.username_placeholder")}
              maxLength={30}
              value={username}
              readOnly={sent}
              aria-invalid={!!errors.username}
              aria-describedby={errors.username ? "err-username" : undefined}
              onChange={(e) => setUsername(e.target.value)}
            />
            {errText("username")}
          </FormField>
        )}
        {mode === "register" && (
          <>
            <FormField as="div">
              <ConsentRow>
                <input
                  type="checkbox"
                  checked={agreeTerms}
                  aria-invalid={!!errors.terms}
                  aria-describedby={errors.terms ? "err-terms" : undefined}
                  onChange={(e) => setAgreeTerms(e.target.checked)}
                />
                <span>
                  {t("auth.agree_terms_prefix")}{" "}
                  <DocLink to="/terms">{t("legal.terms")}</DocLink>{" "}
                  {t("auth.agree_terms_and")}{" "}
                  <DocLink to="/privacy">{t("legal.privacy")}</DocLink>{" "}
                  <small>({t("legal.doc_new_tab")})</small>
                </span>
              </ConsentRow>
              {errText("terms")}
            </FormField>
            <FormField as="div">
              <ConsentRow>
                <input
                  type="checkbox"
                  checked={agreePd}
                  aria-invalid={!!errors.pd}
                  aria-describedby={errors.pd ? "err-pd" : undefined}
                  onChange={(e) => setAgreePd(e.target.checked)}
                />
                <span>
                  {t("auth.agree_pd_prefix")}{" "}
                  <DocLink to="/personal-data-consent">{t("legal.consent")}</DocLink>{" "}
                  {t("auth.agree_pd_suffix")}
                </span>
              </ConsentRow>
              {errText("pd")}
            </FormField>
          </>
        )}
        {!sent ? (
          <Action $variant="primary" type="submit" disabled={busy} aria-busy={busy}>
            <FiMail aria-hidden="true" />
            {busy ? t("auth.sending") : t("auth.get_code")}
          </Action>
        ) : (
          <>
            <Note role="status">
              {t("auth.code_sent_to", { email: email.trim() })}{" "}
              <button
                type="button"
                style={{ textDecoration: "underline" }}
                onClick={changeEmail}
              >
                {t("auth.change_email")}
              </button>
            </Note>
            <FormField>
              {t("auth.code")}
              <InlineCode>
                <Field
                  aria-label={t("auth.code")}
                  placeholder="000000"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  autoFocus
                  aria-invalid={!!errors.code}
                  aria-describedby={errors.code ? "err-code" : undefined}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                />
                <Action
                  type="button"
                  disabled={busy || cooldown > 0}
                  onClick={send}
                >
                  {cooldown > 0
                    ? t("auth.resend_in", { n: cooldown })
                    : t("auth.resend")}
                </Action>
              </InlineCode>
              {errText("code")}
              <Note style={{ fontSize: 14 }}>{t("auth.code_hint")}</Note>
            </FormField>
            <FormField>
              {mode === "register" ? t("auth.password") : t("auth.new_password")}
              <PasswordBox>
                <Field
                  placeholder={t("auth.password_placeholder")}
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  value={password}
                  aria-invalid={!!errors.password}
                  aria-describedby={`pw-hint${errors.password ? " err-password" : ""}`}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  aria-label={showPassword ? t("auth.hide_password") : t("auth.show_password")}
                  aria-pressed={showPassword}
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </PasswordBox>
              {errText("password")}
              <Note id="pw-hint" style={{ fontSize: 14 }}>
                {t("auth.password_rules")}
              </Note>
            </FormField>
            <Action $variant="primary" type="submit" disabled={busy} aria-busy={busy}>
              {busy
                ? t("auth.wait")
                : mode === "register"
                  ? t("auth.register.title")
                  : t("auth.save_password")}
              <FiArrowRight aria-hidden="true" />
            </Action>
          </>
        )}
        {formError && <p role="alert">{formError}</p>}
      </form>
      {mode === "register" && (
        <>
          <Divider>{t("auth.or_steam")}</Divider>
          <Action
            type="button"
            disabled={busy}
            onClick={() => {
              const next: Record<string, string> = {};
              consentErrors(next);
              setErrors(next);
              if (Object.keys(next).length) return;
              steamLogin({ terms: agreeTerms, personalData: agreePd }).catch((e) => toast.error(e.message));
            }}
          >
            <FaSteam />
            {t("auth.continue_steam")}
          </Action>
        </>
      )}
      <AuthFooter>
        {mode === "register" ? t("auth.have_account") : t("auth.remembered")}
        <Link to="/login">{t("auth.sign_in")}</Link>
      </AuthFooter>
    </AuthShell>
  );
}

const STEAM_ERRORS = ["consent", "conflict", "expired", "invalid", "unavailable", "failed"];

export function SteamCallback() {
  const ran = useRef(false),
    nav = useNavigate();
  const { t } = useLanguage();
  const authenticated = useAuthStore((s) => s.isAuthenticated);
  const [error, setError] = useState("");
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const hash = new URLSearchParams(window.location.hash.slice(1));
    const ticket = hash.get("ticket");
    const code = hash.get("error") || hash.get("steam");
    window.history.replaceState(null, "", "/steam-callback");
    if (code && STEAM_ERRORS.includes(code)) {
      setError(code);
      return;
    }
    if (!ticket) {
      setError("failed");
      return;
    }
    request("/auth/steam/exchange", { ticket })
      .then((data) => {
        acceptSession(data);
        nav("/profile", { replace: true });
      })
      .catch((e) => setError(/уже привязан/i.test(e.message) ? "conflict" : "failed"));
  }, [nav]);
  return (
    <AuthShell
      title={error ? t(`steam.${error}.title`) : t("steam.connecting")}
      description={error ? t(`steam.${error}.text`) : t("steam.connecting_text")}
    >
      <Badge $active={!error}>
        <FaSteam />
        {error ? t("steam.retry_needed") : t("steam.checking")}
      </Badge>
      {error && (
        <div style={{ display: "grid", gap: 10 }}>
          <Action
            as={Link}
            to={authenticated ? "/profile" : "/login"}
            $variant="primary"
          >
            {authenticated ? t("steam.back_profile") : t("steam.back_login")}
          </Action>
          {error === "consent" && (
            <Action as={Link} to="/register">
              {t("auth.register.title")}
            </Action>
          )}
          {error === "conflict" && (
            <Action
              type="button"
              onClick={() => steamLogin().catch((e) => toast.error(e.message))}
            >
              <FaSteam />
              {t("steam.other_account")}
            </Action>
          )}
          {authenticated && (
            <Action as={Link} to="/support" $variant="ghost">
              {t("steam.contact_support")}
            </Action>
          )}
        </div>
      )}
    </AuthShell>
  );
}
